import { ComponentType, Suspense } from 'react';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider } from 'antd';
import { createBrowserHistory } from 'history';
import { ProtectedRouteProvider } from 'providers/ProtectedRouteProvider';
import ReactDOM from 'react-dom/client';
import { PersistGate } from 'redux-persist/integration/react';
import { queryClient } from 'stores/store-client';
import 'theme/flexboxgrid.css';
import 'theme/fonts.less';
import 'theme/light.css';
import 'theme/override.less';
import { FallbackContainer } from 'components/UI/FallbackContainer';
import { persistor, store } from 'appRedux/store';
import { lazy } from 'utils/generic/lazy';
import { shouldShowFallbackContainer } from 'utils/widget/should-show-fallback-container';
import { configure } from 'config';
import { Configs } from 'constants/app/configs';
import { GLOBALS, GLOBALS_DEFAULTS } from 'constants/app/global-variables';

const { App } = lazy(() => import(/* webpackChunkName: "AppDev" */ './App'));

const history = createBrowserHistory();

/* eslint-disable */
const { TopArtists } =            lazy(() => import(/* webpackChunkName: "ArtistsWidget" */             'elements/Artists'));
const { NFTs } =                  lazy(() => import(/* webpackChunkName: "NFTsWidget" */                'elements/NFTs'));
const { NFTsGrid } =              lazy(() => import(/* webpackChunkName: "NFTsGridWidget" */            'elements/NFTsGrid'));
const { NFTsVeniceGrid } =        lazy(() => import(/* webpackChunkName: "NFTsVeniceGrid" */            'elements/NFTsVeniceGrid'));
const { NFTsFilter } =            lazy(() => import(/* webpackChunkName: "NFTsFilter" */                'elements/NFTsFilter'));
const { CollectionsCarousel } =   lazy(() => import(/* webpackChunkName: "CollectionsWidget" */         'elements/Collections'));
const { CollectionsRanking } =    lazy(() => import(/* webpackChunkName: "CollectionsRanking" */        'elements/CollectionsRanking'));
const { Account } =               lazy(() => import(/* webpackChunkName: "AccountWidget" */             'elements/Account'));
const { Explore } =               lazy(() => import(/* webpackChunkName: "Explore" */                   'elements/Explore'));
const { Common } =                lazy(() => import(/* webpackChunkName: "CommonWidget" */              'elements/Common'));
const { ClaimNFT } =              lazy(() => import(/* webpackChunkName: "ClaimNFTWidget" */            'elements/ClaimNFT'));
const { CartWidget } =            lazy(() => import(/* webpackChunkName: "CartWidget" */                'elements/Cart'));
const { OmniSearch } =            lazy(() => import(/* webpackChunkName: "OmniSearch" */                'elements/OmniSearch'));
const { NftSearch } =             lazy(() => import(/* webpackChunkName: "NftSearch" */                 'elements/NftSearch'));
const { FeaturedCollections } =   lazy(() => import(/* webpackChunkName: "FeaturedCollections" */       'elements/FeaturedCollections'));
/* eslint-enable */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderWidget = (Element: ComponentType<{ configs: any }>, name: string) =>
  new Promise(resolve => {
    const widgetContainers = document.querySelectorAll(
      `[data-widget="${name}"][data-loaded="false"]`
    );

    if (!widgetContainers.length) {
      resolve(true);

      return;
    }

    widgetContainers.forEach(element => {
      let configs = {};

      try {
        configs = JSON.parse(element.getAttribute('data-configs') || '{}');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }

      ReactDOM.createRoot(element).render(
        <Suspense
          fallback={
            shouldShowFallbackContainer(name, configs) ? (
              <FallbackContainer />
            ) : null
          }
        >
          <ConfigProvider>
            <QueryClientProvider client={queryClient}>
              <Provider store={store}>
                <PersistGate loading={null} persistor={persistor}>
                  <Router history={history}>
                    <ProtectedRouteProvider
                      protectedRoutes={Configs.PROTECTED_ROUTES}
                    >
                      <Element configs={configs} />
                    </ProtectedRouteProvider>
                  </Router>
                </PersistGate>
              </Provider>
            </QueryClientProvider>
          </ConfigProvider>
        </Suspense>
      );

      setTimeout(() => element.setAttribute('data-loaded', 'true'), 50);
    });
    resolve(true);
  });

window.initWidgets = async () => {
  if (GLOBALS.app.isInitProcess) {
    return;
  }
  GLOBALS.app.isInitProcess = true;
  // reset navigation defaults
  GLOBALS.navigation = GLOBALS_DEFAULTS.navigation;
  await configure();
  await renderWidget(Common, 'CommonElements');
  Promise.all([
    renderWidget(Account, 'NFTAccount'),
    renderWidget(NFTs, 'NFTBids'),
    renderWidget(NFTsGrid, 'NFTsGrid'),
    renderWidget(NFTsVeniceGrid, 'NFTsVeniceGrid'),
    renderWidget(NFTsFilter, 'NFTsFilter'),
    renderWidget(Explore, 'Explore'),
    renderWidget(TopArtists, 'NFTArtists'),
    renderWidget(CollectionsCarousel, 'NFTCollections'),
    renderWidget(CollectionsRanking, 'CollectionsRanking'),
    renderWidget(ClaimNFT, 'ClaimNFT'),
    renderWidget(CartWidget, 'CartWidget'),
    renderWidget(OmniSearch, 'OmniSearch'),
    renderWidget(NftSearch, 'NFTsSearch'),
    renderWidget(FeaturedCollections, 'FeaturedCollections')
  ]).then(() => {
    GLOBALS.app.isInitProcess = false;
  });
};

(async () => {
  if (process.env.REACT_APP_ENV !== 'widgets') {
    await configure();
    const root = ReactDOM.createRoot(
      document.getElementById('root') as HTMLElement
    );
    root.render(
      <Suspense fallback={null}>
        <ConfigProvider>
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <Router history={history}>
                  <ProtectedRouteProvider
                    protectedRoutes={Configs.PROTECTED_ROUTES}
                  >
                    <App />
                  </ProtectedRouteProvider>
                </Router>
              </PersistGate>
            </Provider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </ConfigProvider>
      </Suspense>
    );
  }
})();
